import { useState, useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { useApps } from '@/hooks/useApps'
import { useAllImprovements, type Improvement, type ImprovementStatus } from '@/hooks/useImprovements'
import { useEnqueueBulkGenerate } from '@/hooks/useBulkJobs'
import { SectionHeader, Card, Button, Badge, Skeleton, Empty, Input } from '@/components/ui'
import { ImprovementCard } from '@/components/ImprovementCard'
import { TrendingUp, ListChecks, Sparkles, Search, Filter, ChevronDown } from 'lucide-react'
import { cn, ENTITIES } from '@/lib/utils'

type StatusFilter = 'all' | ImprovementStatus
type ImpactFilter = 'all' | 'high' | 'medium' | 'low'
type GroupMode = 'app' | 'status' | 'impact'

export function ValueEngineeringPage() {
  const { items, loading } = useAllImprovements()
  const { data: apps } = useApps()
  const enqueue = useEnqueueBulkGenerate()

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [impactFilter, setImpactFilter] = useState<ImpactFilter>('all')
  const [entityFilter, setEntityFilter] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [groupMode, setGroupMode] = useState<GroupMode>('app')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    return items.filter(i => {
      if (statusFilter !== 'all' && i.status !== statusFilter) return false
      if (impactFilter !== 'all' && i.impact !== impactFilter) return false
      if (entityFilter !== 'all' && i.app_entity !== entityFilter) return false
      if (q) {
        const hay = `${i.title} ${i.rationale} ${i.expected_uplift} ${i.app_name} ${i.category}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [items, statusFilter, impactFilter, entityFilter, search])

  const counts = useMemo(() => {
    const apps_audited = new Set(items.map(i => i.app_slug)).size
    const total_apps = apps?.length ?? 0
    return {
      total: items.length,
      suggested: items.filter(i => i.status === 'suggested').length,
      in_progress: items.filter(i => i.status === 'in-progress').length,
      done: items.filter(i => i.status === 'done').length,
      dismissed: items.filter(i => i.status === 'dismissed').length,
      high_impact_open: items.filter(i => i.impact === 'high' && (i.status === 'suggested' || i.status === 'in-progress')).length,
      apps_audited, total_apps,
      // Clamp: items can resolve before apps loads, which would briefly show a
      // negative unaudited count in the header.
      apps_unaudited: Math.max(0, total_apps - apps_audited),
    }
  }, [items, apps])

  function queueAuditAll() {
    enqueue.mutate({ kinds: ['improvement_audit'], force: false })
  }
  function queueReauditAll() {
    enqueue.mutate({ kinds: ['improvement_audit'], force: true })
  }

  const grouped = useMemo(() => {
    if (groupMode === 'app') {
      const m = new Map<string, Improvement[]>()
      for (const i of filtered) {
        const key = `${i.app_slug}|${i.app_name}`
        if (!m.has(key)) m.set(key, [])
        m.get(key)!.push(i)
      }
      return Array.from(m.entries()).map(([key, list]) => {
        const [slug, name] = key.split('|')
        return { key, label: name, slug, items: list.sort(impactSort) }
      }).sort((a, b) => b.items.length - a.items.length)
    }
    if (groupMode === 'status') {
      const order: ImprovementStatus[] = ['in-progress', 'suggested', 'done', 'dismissed']
      return order.map(s => ({
        key: s, label: s.charAt(0).toUpperCase() + s.slice(1),
        slug: null, items: filtered.filter(i => i.status === s).sort(impactSort),
      })).filter(g => g.items.length > 0)
    }
    return ['high', 'medium', 'low'].map(im => ({
      key: im, label: `${im[0].toUpperCase() + im.slice(1)} impact`,
      slug: null, items: filtered.filter(i => i.impact === (im as Improvement['impact'])).sort(impactSort),
    })).filter(g => g.items.length > 0)
  }, [filtered, groupMode])

  return (
    <div className="space-y-8">
      <SectionHeader
        eyebrow="Value Engineering"
        title="Codebase audit · concrete moves to raise exit value"
        description="Like a home stager's report, but for code. Each suggestion comes with a copy-paste-ready LLM prompt to execute it. Re-audit any time — completed items inform the next iteration."
        action={
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" loading={enqueue.isPending} onClick={queueReauditAll}>
              <Sparkles size={12} /> Re-audit all (force)
            </Button>
            <Button loading={enqueue.isPending} onClick={queueAuditAll}>
              <Sparkles size={14} /> Audit unaudited ({counts.apps_unaudited})
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI eyebrow="Apps audited" value={counts.apps_audited} sub={`of ${counts.total_apps}`} tone="bronze" />
        <KPI eyebrow="Open · suggested" value={counts.suggested} sub="awaiting action" tone="gold" />
        <KPI eyebrow="In progress" value={counts.in_progress} sub="actively shipping" tone="info" />
        <KPI eyebrow="Done" value={counts.done} sub="value captured" tone="success" />
        <KPI eyebrow="HIGH-impact open" value={counts.high_impact_open} sub="biggest movers" tone="warning" big />
      </div>

      <Card className="!p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/40" />
            <Input className="pl-9" placeholder="Search title, rationale, app…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <FilterChip label="Status" value={statusFilter} onChange={(v) => setStatusFilter(v as StatusFilter)} options={['all', 'suggested', 'in-progress', 'done', 'dismissed']} />
          <FilterChip label="Impact" value={impactFilter} onChange={(v) => setImpactFilter(v as ImpactFilter)} options={['all', 'high', 'medium', 'low']} />
          <FilterChip label="Entity" value={entityFilter} onChange={setEntityFilter} options={['all', ...ENTITIES]} />
          <div className="flex items-center gap-1 ml-auto">
            <span className="eyebrow">Group:</span>
            {(['app', 'status', 'impact'] as GroupMode[]).map(g => (
              <button key={g} onClick={() => setGroupMode(g)} className={cn(
                'px-2.5 py-1 rounded font-mono text-[10px] uppercase tracking-[0.1em] transition-colors',
                groupMode === g ? 'bg-gold/15 text-gold-soft' : 'text-cream/55 hover:text-cream',
              )}>{g}</button>
            ))}
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Empty
          icon={<TrendingUp size={48} />}
          title={items.length === 0 ? "No audits run yet" : "No items match these filters"}
          description={items.length === 0
            ? "Click Audit unaudited to queue improvement audits across the portfolio. The bulk worker processes 8 apps every minute."
            : "Adjust filters or clear search to see more."}
          action={items.length === 0 ? (
            <Button onClick={queueAuditAll} loading={enqueue.isPending}>
              <Sparkles size={14} /> Run audits
            </Button>
          ) : null}
        />
      ) : (
        <div className="space-y-6">
          {grouped.map(group => {
            const isCollapsed = collapsed.has(group.key)
            const open = group.items.filter(i => i.status === 'suggested' || i.status === 'in-progress').length
            return (
              <section key={group.key}>
                <div className="flex items-center gap-3 w-full mb-3 pb-2 border-b border-bronze/15 hover:border-bronze/40 transition-colors">
                  <button
                    type="button"
                    aria-label={isCollapsed ? 'Expand group' : 'Collapse group'}
                    onClick={() => setCollapsed(s => {
                      const n = new Set(s)
                      if (n.has(group.key)) n.delete(group.key)
                      else n.add(group.key)
                      return n
                    })}
                    className="flex items-center shrink-0"
                  >
                    <ChevronDown size={14} className={cn('text-cream/55 transition-transform', isCollapsed && '-rotate-90')} />
                  </button>
                  {group.slug ? (
                    <Link
                      to="/apps/$slug"
                      params={{ slug: group.slug }}
                      className="font-heading text-lg text-cream hover:text-gold-soft"
                    >
                      {group.label}
                    </Link>
                  ) : (
                    <h3 className="font-heading text-lg text-cream">{group.label}</h3>
                  )}
                  <Badge tone="cream" className="!py-0">{group.items.length}</Badge>
                  {open > 0 && <Badge tone="gold" className="!py-0">{open} open</Badge>}
                </div>
                {!isCollapsed && (
                  <div className="space-y-2">
                    {group.items.map(item => <ImprovementCard key={item.id} item={item} showApp={groupMode !== 'app'} />)}
                  </div>
                )}
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}

function KPI({ eyebrow, value, sub, tone, big = false }: {
  eyebrow: string; value: number; sub: string; tone: string; big?: boolean
}) {
  const color = ({
    gold: 'text-gold-soft', info: 'text-info', success: 'text-success',
    warning: 'text-warning', bronze: 'text-bronze',
  } as Record<string, string>)[tone] ?? 'text-cream'
  return (
    <div className={cn('glass rounded-lg p-4', big && 'border-warning/40')}>
      <div className={cn('eyebrow mb-2', color)}>{eyebrow}</div>
      <div className={cn('font-display italic font-light leading-none mb-1 text-cream', big ? 'text-5xl' : 'text-4xl')}>{value}</div>
      <div className="font-mono text-[10px] text-cream/45">{sub}</div>
    </div>
  )
}

function FilterChip({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: string[]
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Filter size={11} className="text-cream/40" />
      <span className="eyebrow">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-navy/50 border border-cream/15 rounded px-2 py-1 text-xs text-cream focus:border-gold-soft focus:outline-none cursor-pointer"
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )
}

function impactSort(a: Improvement, b: Improvement): number {
  const order = { high: 0, medium: 1, low: 2 }
  const sa = order[a.impact] - order[b.impact]
  if (sa !== 0) return sa
  const eo = { quick: 0, medium: 1, deep: 2 }
  return eo[a.effort] - eo[b.effort]
}

void ListChecks
